import { fetch } from 'cross-fetch';
import * as Network from 'expo-network';
import { NetworkError, NetworkErrorCode } from 'lib/errors';
import { isError } from 'lib/isError';
import { noop } from 'lodash';
import { Platform } from 'react-native';
import * as http from 'service/api/constants';
import { SET_LAST_OFFLINE_TIME, SET_LAST_ONLINE_TIME } from 'store/actions';
import {
  selectAppInstance,
  selectIsOffline,
  selectLastOnlineTime,
} from 'store/app/selectors';
import { selectAuthToken } from 'store/auth/selectors';
import {
  SET_HAS_NETWORK_ERROR,
  SET_IS_UNAUTHORIZED,
} from 'store/error/actions';
import { call, put, race, select } from 'typed-redux-saga';

import { randomKey } from 'lib/riskbase';
import delay from 'delay';
import { version as appVersion } from '../../version.json';
import {
  DeleteEndpoint,
  GetEndpoint,
  PostEndpoint,
  PutEndpoint,
} from 'types/Endpoint';

type HasInternet = {
  throwErrorIfOffline: boolean;
  forcePing: boolean;
  doPingIfWeb: boolean;
};

export const OFFLINE_ERROR = new NetworkError(
  NetworkErrorCode.OFFLINE,
  'An internet connection is required to use this feature; ' +
    'please check your device has a connection and try again',
);

const CANCELLED_ERROR = new NetworkError(
  NetworkErrorCode.CANCELLED,
  'RiskBase cancelled the connection because a new one was started',
);

type APIOptions = {
  skipConnectionCheck?: boolean;
  abortKey?: string;
  multiformData?: boolean;
  fileName?: string;
};

const abortSignals: Record<string, AbortController> = {};

export function* checkInternetConnection({
  throwErrorIfOffline,
  forcePing,
  doPingIfWeb,
}: HasInternet): Generator<unknown, boolean, unknown> {
  const thinksIsOnline = !(yield* select(selectIsOffline));
  const lastOnlineTime = (yield* select(selectLastOnlineTime)) || 0;
  let isOnline = false;

  try {
    // Check for any network existence
    if ((yield* call(Network.getNetworkStateAsync)).isConnected) {
      // Don't ping if web. Ping is to check if a half decent internet connection is available,
      // which may be a problem with mobile devices but is less of an issue with web
      if (!doPingIfWeb && Platform.OS === 'web') {
        return true;
      }

      // Don't do ping if last check was online and confirmed less than 2 seconds ago
      if (!forcePing && thinksIsOnline && lastOnlineTime > Date.now() - 2000) {
        isOnline = true;
      } else {
        // Do short timeout ping for internet
        const abortKey = randomKey();
        abortSignals[abortKey] = new AbortController();
        const { check } = yield* race({
          check: call(fetch, http.PING, {
            signal: abortSignals[abortKey]?.signal,
            headers: {
              'Content-Type': 'application/json;charset=utf-8',
              'RB-App-Client':
                Platform.OS === 'ios'
                  ? 'ios'
                  : Platform.OS === 'android'
                    ? 'android'
                    : 'web',
              'RB-App-Instance': yield* select(selectAppInstance),
              'RB-App-Version': appVersion,
            },
            method: 'GET',
          }),
          timeout: delay(4000),
        });
        if (check?.status === 200) {
          isOnline = true;
        } else if (check === undefined) {
          yield* call(apiAbort, abortKey);
        }
      }
    }
  } catch (_) {
    noop();
  }

  if (isOnline) {
    yield* put(SET_LAST_ONLINE_TIME(Date.now()));
  } else {
    yield* put(SET_LAST_OFFLINE_TIME(Date.now()));
  }

  if (!isOnline && throwErrorIfOffline) {
    throw OFFLINE_ERROR;
  }

  return isOnline;
}

function* makeRequest<E extends Record<string, unknown>>({
  body,
  endpoint,
  method,
  responseType = 'json',
  checkConnection = true,
  abortKey,
  multiformData,
}: {
  endpoint: string;
  body?: string | FormData;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  responseType?: 'json' | 'blob';
  checkConnection?: boolean;
  abortKey?: string;
  multiformData?: boolean;
  fileName?: string;
}): Generator<unknown, E, unknown> {
  let response: Response;
  let responseBody: E;
  let abortSignalInternal: AbortSignal | undefined;

  if (abortKey) {
    yield* call(apiAbort, abortKey);
    abortSignals[abortKey] = new AbortController();
    abortSignalInternal = abortSignals[abortKey]?.signal;
  }

  if (checkConnection) {
    yield* call(checkInternetConnection, {
      throwErrorIfOffline: true,
      forcePing: false,
      doPingIfWeb: false,
    });
  }

  try {
    response = yield* call(fetch, endpoint, {
      signal: abortSignalInternal,
      body,
      headers: {
        Authorization: `Bearer ${
          (yield* select(selectAuthToken)) || 'NoToken'
        }`,
        ...(multiformData
          ? {
              // Don't set Content-Type for multipart - let the browser set it with correct boundary
              Accept: '*/*',
              Connection: 'keep-alive', // keep-alive for large uploads
            }
          : {
              Accept: 'application/json',
              'Content-Type': 'application/json;charset=utf-8',
            }),
        'RB-App-Client':
          Platform.OS === 'ios'
            ? 'ios'
            : Platform.OS === 'android'
              ? 'android'
              : 'web',
        'RB-App-Instance': yield* select(selectAppInstance),
        'RB-App-Version': appVersion,
      },
      method,
    });

    responseBody =
      response.status === 204
        ? {}
        : yield* call([response, response[responseType]]);
    yield* put(SET_LAST_ONLINE_TIME(Date.now()));
  } catch (error) {
    if (isError(error) && abortSignalInternal?.aborted) {
      throw CANCELLED_ERROR;
    }
    yield* put(SET_LAST_OFFLINE_TIME(Date.now()));
    throw OFFLINE_ERROR;
  }

  if (response.status === NetworkErrorCode.TOOMANYREQUESTS) {
    throw new NetworkError(
      NetworkErrorCode.TOOMANYREQUESTS,
      // 'The app has performed too many requests in a short amount of time. Please try again in a bit',
      (responseBody.error as string) || 'Too Many Requests. Rate Limit Reached',
    );
  } else if (
    response.status === NetworkErrorCode.ERROR ||
    response.status === NetworkErrorCode.SERVER
  ) {
    throw new NetworkError(
      NetworkErrorCode.ERROR,
      (responseBody.error as string) ||
        "There was an error but we don't know what it was",
    );
  } else if (response.status === NetworkErrorCode.UNAUTHORIZED) {
    yield* put(SET_IS_UNAUTHORIZED());
    throw new NetworkError(
      NetworkErrorCode.UNAUTHORIZED,
      (responseBody.error as string) || 'User in unauthorized',
    );
  } else if (
    response.status !== 200 &&
    response.status !== 201 &&
    response.status !== 204
  ) {
    yield* put(SET_HAS_NETWORK_ERROR());
    throw new NetworkError(
      NetworkErrorCode.UNKNOWN,
      (responseBody.error as string) || 'Network error on the server',
    );
  }

  return responseBody;
}

export function* apiAbort(abortKey: string) {
  const abortSignal = abortSignals[abortKey];
  if (abortSignal) {
    abortSignals[abortKey]?.abort();
    delete abortSignals[abortKey];
  }
}

export function* apiGet<Response extends Record<string, unknown>>(
  endpoint: (typeof http)[keyof typeof http],
  options: APIOptions = {},
): Generator<unknown, Response> {
  return yield* makeRequest({
    endpoint: endpoint as string,
    method: 'GET',
    checkConnection: !options.skipConnectionCheck,
    abortKey: options.abortKey,
  });
}

export function* apiPost<
  Response extends Record<string, unknown>,
  Payload extends Record<string, unknown> | string | undefined =
    | Record<string, unknown>
    | undefined,
>(
  endpoint: string,
  payload?: Payload,
  options: APIOptions = {},
): Generator<unknown, Response> {
  return (yield* makeRequest({
    body: payload ? JSON.stringify(payload) : undefined,
    endpoint,
    method: 'POST',
    checkConnection: !options.skipConnectionCheck,
    abortKey: options.abortKey,
  })) as Response;
}

type QueryParamValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | (string | number | boolean | null | undefined)[];

function appendQueryValue(
  searchParams: URLSearchParams,
  key: string,
  value: QueryParamValue,
) {
  if (value === undefined || value === null) {
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((arrayValue) => {
      appendQueryValue(searchParams, key, arrayValue);
    });
    return;
  }
  searchParams.append(key, String(value));
}

function getQueryString(searchParams: URLSearchParams): string {
  const string = searchParams.toString();
  return string ? `?${string}` : '';
}

function buildUrl(
  api: http.Api,
  template: string,
  params: Record<string, string>,
  query?: Record<string, QueryParamValue>,
): string {
  const searchParams = new URLSearchParams();
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      appendQueryValue(searchParams, key, value);
    });
  }

  return (
    http.ApiUrl[api] +
    template.replace(/:([a-zA-Z0-9_]+)/g, (_, key) => {
      if (params[key] === undefined) {
        throw new Error(`Missing param: ${key}`);
      }
      return encodeURIComponent(params[key]);
    }) +
    getQueryString(searchParams)
  );
}

export function* apiPut<
  Response extends Record<string, unknown>,
  Payload extends Record<string, unknown> | undefined =
    | Record<string, unknown>
    | undefined,
>(
  endpoint: string,
  payload: Payload,
  options: APIOptions = {},
): Generator<unknown, Response> {
  return yield* makeRequest({
    body: JSON.stringify(payload),
    endpoint,
    method: 'PUT',
    checkConnection: !options.skipConnectionCheck,
    abortKey: options.abortKey,
  });
}

export function* apiRemove(
  endpoint: string,
  options: APIOptions = {},
): unknown {
  return yield* makeRequest({
    endpoint,
    method: 'DELETE',
    checkConnection: !options.skipConnectionCheck,
    abortKey: options.abortKey,
  });
}

type QueryArg<Q> = [keyof Q] extends [never]
  ? { query?: undefined }
  : { query: Q };

export function* requestGet<EP extends GetEndpoint>(
  config: {
    api: http.Api;
    url: EP['url'];
    params: EP['params'];
  } & QueryArg<EP['query']>,
  options: APIOptions = {},
): Generator<unknown, EP['response'], unknown> {
  return (yield* makeRequest({
    endpoint: buildUrl(
      config.api,
      config.url,
      config.params || {},
      config.query || {},
    ),
    method: 'GET',
    body: undefined,
    checkConnection: !options.skipConnectionCheck,
    abortKey: options.abortKey,
  })) as EP['response'];
}

export function* requestDelete<EP extends DeleteEndpoint>(
  config: {
    api: http.Api;
    url: EP['url'];
    params: EP['params'];
  } & QueryArg<EP['query']>,
  options: APIOptions = {},
): Generator<unknown, EP['response'], unknown> {
  return (yield* makeRequest({
    endpoint: buildUrl(
      config.api,
      config.url,
      config.params || {},
      config.query || {},
    ),
    method: 'DELETE',
    body: undefined,
    checkConnection: !options.skipConnectionCheck,
    abortKey: options.abortKey,
  })) as EP['response'];
}

export function* requestPost<EP extends PostEndpoint>(
  config: {
    api: http.Api;
    url: EP['url'];
    body: EP['body'];
    params: EP['params'];
  } & QueryArg<EP['query']>,
  options: APIOptions = {},
): Generator<unknown, EP['response'], unknown> {
  return (yield* makeRequest({
    endpoint: buildUrl(
      config.api,
      config.url,
      config.params || {},
      config.query || {},
    ),
    method: 'POST',
    body: config.body
      ? options.multiformData
        ? config.body
        : JSON.stringify(config.body)
      : undefined,
    checkConnection: !options.skipConnectionCheck,
    abortKey: options.abortKey, // content type -> ensure correct type is sent
    multiformData: options.multiformData,
    fileName: options.fileName,
  })) as EP['response'];
}

export function* requestPut<EP extends PutEndpoint>(
  config: {
    api: http.Api;
    url: EP['url'];
    body: EP['body'];
    params: EP['params'];
  } & QueryArg<EP['query']>,
  options: APIOptions = {},
): Generator<unknown, EP['response'], unknown> {
  return (yield* makeRequest({
    endpoint: buildUrl(
      config.api,
      config.url,
      config.params || {},
      config.query || {},
    ),
    method: 'PUT',
    body: config.body ? JSON.stringify(config.body) : undefined,
    checkConnection: !options.skipConnectionCheck,
    abortKey: options.abortKey,
  })) as EP['response'];
}
